import {
  FileText,
  Landmark,
  Receipt,
  ScrollText,
  TrendingUp,
  Wallet,
  Wrench,
} from 'lucide-react';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { clientAuthorizedGet } from 'utils/fetchUtils';
import { translate } from 'utils/i18nTranslate';
import { formatListDisplayDate } from 'utils/dateUtils';
import {
  getCashDisbursementJournalEntryViewPath,
  getCashReceiptsJournalEntryViewPath,
} from 'utils/transactionSourceUtils';
import type {
  ApBill,
  ApBillFxRevaluationListItem,
  ApBillPaymentListItem,
} from 'types/apBill';
import type { ArInvoice, ArInvoicePayment } from 'types/arInvoice';
import type { CashReceiptsJournalEntry } from 'types/cashReceiptsJournal';
import type { CashDisbursementJournalEntry } from 'types/cashDisbursementJournal';
import type { CapitalListItem } from 'types/capital';
import type {
  EquipmentDepreciationReportRow,
  EquipmentListItem,
} from 'types/equipment';
import type { Transaction } from 'types/transaction';
import type { PaginatedResponse } from 'types/pagination';
import type {
  CommandPaletteBookReferenceHandler,
  CommandPaletteBookReferenceResult,
  CommandPaletteBookReferenceSearchContext,
} from 'types/commandPaletteBookReference';

type ArInvoiceListResponse = PaginatedResponse<ArInvoice>;
type CashReceiptsJournalListResponse = {
  items: CashReceiptsJournalEntry[];
  total: number;
  page: number;
  limit: number;
};
type CashDisbursementJournalListResponse = {
  items: CashDisbursementJournalEntry[];
  total: number;
  page: number;
  limit: number;
};

function mapArInvoiceToBookReferenceResult(
  invoice: ArInvoice,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = invoice.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const customerName = invoice.customerName?.trim() || '—';

  return {
    id: `ar_invoice:${invoice.id}`,
    kind: 'ar_invoice',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.arInvoiceSubtitle', {
      customer: customerName,
      invoiceNumber: invoice.invoiceNumber,
    }),
    path: ROUTES.AR_INVOICES.DETAIL(invoice.id),
    icon: FileText,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchArInvoiceBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const response = await clientAuthorizedGet<ArInvoiceListResponse>(
    API_ROUTES.AR_INVOICES,
    {
      search: context.referenceQuery,
      page: '1',
      limit: '10',
      includeVoided: 'true',
      status: 'all',
      due: 'all',
    },
    context.companyId,
  );

  return response.data
    .map(mapArInvoiceToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapCashReceiptToBookReferenceResult(
  entry: CashReceiptsJournalEntry,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = entry.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const customerName =
    entry.customerName?.trim() ||
    (typeof entry.customer === 'object' ? entry.customer?.name?.trim() : '') ||
    '—';

  return {
    id: `cash_receipt:${entry.id}`,
    kind: 'cash_receipt',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.cashReceiptSubtitle', {
      customer: customerName,
      orNumber: entry.officialReceiptNumber,
    }),
    path: getCashReceiptsJournalEntryViewPath(entry.id),
    icon: Receipt,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchCashReceiptBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const response = await clientAuthorizedGet<CashReceiptsJournalListResponse>(
    API_ROUTES.CASH_RECEIPTS_JOURNAL,
    {
      search: context.referenceQuery,
      page: '1',
      limit: '10',
      includeVoided: 'true',
    },
    context.companyId,
  );

  return response.items
    .map(mapCashReceiptToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapCashDisbursementToBookReferenceResult(
  entry: CashDisbursementJournalEntry,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = entry.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const supplierName =
    entry.supplierName?.trim() ||
    (typeof entry.supplier === 'object' ? entry.supplier?.name?.trim() : '') ||
    '—';

  return {
    id: `cash_disbursement:${entry.id}`,
    kind: 'cash_disbursement',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate(
      'commandPalette.bookReference.cashDisbursementSubtitle',
      {
        supplier: supplierName,
        orNumber: entry.officialReceiptNumber,
      },
    ),
    path: getCashDisbursementJournalEntryViewPath(entry.id),
    icon: Wallet,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchCashDisbursementBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const response =
    await clientAuthorizedGet<CashDisbursementJournalListResponse>(
      API_ROUTES.CASH_DISBURSEMENT_JOURNAL,
      {
        search: context.referenceQuery,
        page: '1',
        limit: '10',
        includeVoided: 'true',
      },
      context.companyId,
    );

  return response.items
    .map(mapCashDisbursementToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapApBillToBookReferenceResult(
  bill: ApBill,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = bill.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const supplierName = bill.supplierName?.trim() || '—';
  const supplierRef = bill.referenceNumber?.trim() || bill.bookReferenceNumber;

  return {
    id: `ap_bill:${bill.id}`,
    kind: 'ap_bill',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.apBillSubtitle', {
      supplier: supplierName,
      referenceNumber: supplierRef,
    }),
    path: ROUTES.BILLS_LEDGER.DETAIL(bill.id),
    icon: FileText,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchApBillBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const bills = await clientAuthorizedGet<ApBill[]>(
    API_ROUTES.AP_BILLS,
    { includeVoided: 'true' },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return bills
    .filter((bill) => bill.bookReferenceNumber?.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapApBillToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapApBillPaymentToBookReferenceResult(
  payment: ApBillPaymentListItem,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = payment.paymentReference?.trim();
  if (!referenceNumber) {
    return null;
  }

  const supplierName = payment.supplierName?.trim() || '—';
  const billRef =
    payment.referenceNumber?.trim() || payment.bookReferenceNumber || '—';

  return {
    id: `ap_bill_payment:${payment.id}`,
    kind: 'ap_bill_payment',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.apBillPaymentSubtitle', {
      supplier: supplierName,
      billReference: billRef,
    }),
    path: ROUTES.BILL_PAYMENTS.DETAIL(payment.id),
    icon: FileText,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchApBillPaymentBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const payments = await clientAuthorizedGet<ApBillPaymentListItem[]>(
    API_ROUTES.AP_BILLS_ALL_PAYMENTS,
    {},
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return payments
    .filter((payment) => payment.paymentReference.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapApBillPaymentToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

type CapitalListResponse = {
  items: CapitalListItem[];
  total: number;
  page: number;
  limit: number;
};

function mapCapitalToBookReferenceResult(
  entry: CapitalListItem,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = entry.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  return {
    id: `capital:${entry.id}`,
    kind: 'capital',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.capitalSubtitle', {
      description: entry.description?.trim() || '—',
      capitalType: translate(
        `pages.capitals.CapitalList.capitalTypes.${entry.capitalType}`,
      ),
    }),
    path: ROUTES.CAPITALS.DETAIL(entry.id),
    icon: Landmark,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchCapitalBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const response = await clientAuthorizedGet<CapitalListResponse>(
    API_ROUTES.CAPITALS,
    {
      search: context.referenceQuery,
      page: '1',
      limit: '10',
      includeVoided: 'true',
    },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return response.items
    .filter((entry) => entry.bookReferenceNumber.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapCapitalToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapEquipmentToBookReferenceResult(
  item: EquipmentListItem,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = item.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  return {
    id: `equipment:${item.id}`,
    kind: 'equipment',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.equipmentSubtitle', {
      name: item.name?.trim() || '—',
      acquisitionDate: formatListDisplayDate(item.acquisitionDate),
    }),
    path: ROUTES.EQUIPMENT.DETAIL(item.id),
    icon: Wrench,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchEquipmentBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const items = await clientAuthorizedGet<EquipmentListItem[]>(
    API_ROUTES.EQUIPMENT,
    {
      search: context.referenceQuery,
      includeCancelled: 'true',
    },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return items
    .filter((item) => item.bookReferenceNumber.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapEquipmentToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapEquipmentDepreciationToBookReferenceResult(
  row: EquipmentDepreciationReportRow,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = row.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  return {
    id: `equipment_depreciation:${row.id}`,
    kind: 'equipment_depreciation',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate(
      'commandPalette.bookReference.equipmentDepreciationSubtitle',
      {
        equipment: row.equipmentName?.trim() || '—',
        fiscalYear: row.fiscalYear,
      },
    ),
    path: ROUTES.EQUIPMENT.DEPRECIATION_DETAIL(row.id),
    icon: Wrench,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchEquipmentDepreciationBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const rows = await clientAuthorizedGet<EquipmentDepreciationReportRow[]>(
    API_ROUTES.EQUIPMENT_DEPRECIATION_REPORT,
    {
      search: context.referenceQuery,
      includeCancelled: 'true',
    },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return rows
    .filter((row) => row.bookReferenceNumber.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapEquipmentDepreciationToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapApBillFxRevaluationToBookReferenceResult(
  row: ApBillFxRevaluationListItem,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = row.bookReferenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const billLabel =
    row.billBookReferenceNumber?.trim() ||
    row.billReferenceNumber?.trim() ||
    '—';

  return {
    id: `ap_bill_fx_revaluation:${row.id}`,
    kind: 'ap_bill_fx_revaluation',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.fxRevaluationSubtitle', {
      bill: billLabel,
      date: formatListDisplayDate(row.asOfDate) || row.asOfDate,
    }),
    path: ROUTES.FX_REVALUATIONS.DETAIL(row.id),
    icon: TrendingUp,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchApBillFxRevaluationBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const rows = await clientAuthorizedGet<ApBillFxRevaluationListItem[]>(
    API_ROUTES.AP_BILLS_ALL_FX_REVALUATIONS,
    { search: context.referenceQuery },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return rows
    .filter((row) => row.bookReferenceNumber.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapApBillFxRevaluationToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapArInvoicePaymentToBookReferenceResult(
  payment: ArInvoicePayment,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = payment.paymentReference?.trim();
  if (!referenceNumber) {
    return null;
  }

  const customerName = payment.customerName?.trim() || '—';
  const invoiceLabel =
    payment.allocations.length === 0
      ? '—'
      : payment.allocations.length > 1
        ? payment.allocations.map((row) => row.invoiceNumber).join(', ')
        : payment.allocations[0].invoiceNumber;

  return {
    id: `ar_invoice_payment:${payment.id}`,
    kind: 'ar_invoice_payment',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate(
      'commandPalette.bookReference.arInvoicePaymentSubtitle',
      {
        customer: customerName,
        invoiceNumber: invoiceLabel,
      },
    ),
    path: ROUTES.INVOICE_PAYMENTS.DETAIL(payment.id),
    icon: Receipt,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchArInvoicePaymentBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const payments = await clientAuthorizedGet<ArInvoicePayment[]>(
    API_ROUTES.AR_INVOICES_PAYMENTS,
    {},
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return payments
    .filter((payment) => payment.paymentReference.toLowerCase().includes(query))
    .slice(0, 10)
    .map(mapArInvoicePaymentToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

function mapJournalVoucherToBookReferenceResult(
  transaction: Transaction,
): CommandPaletteBookReferenceResult | null {
  const referenceNumber = transaction.referenceNumber?.trim();
  if (!referenceNumber) {
    return null;
  }

  const description = transaction.description?.trim() || '—';
  const dateLabel = transaction.date
    ? formatListDisplayDate(transaction.date)
    : '—';

  return {
    id: `journal_voucher:${transaction.id}`,
    kind: 'journal_voucher',
    referenceNumber,
    title: referenceNumber,
    subtitle: translate('commandPalette.bookReference.journalVoucherSubtitle', {
      description,
      date: dateLabel,
    }),
    path: ROUTES.TRANSACTIONS.VIEW.replace(':id', transaction.id),
    icon: ScrollText,
    sectionKey: 'commandPalette.sections.bookReferences',
  };
}

async function searchJournalVoucherBookReferences(
  context: CommandPaletteBookReferenceSearchContext,
): Promise<CommandPaletteBookReferenceResult[]> {
  const transactions = await clientAuthorizedGet<Transaction[]>(
    API_ROUTES.TRANSACTION,
    { includeVoided: 'true' },
    context.companyId,
  );

  const query = context.referenceQuery.toLowerCase();

  return transactions
    .filter((transaction) =>
      transaction.referenceNumber?.toLowerCase().includes(query),
    )
    .slice(0, 10)
    .map(mapJournalVoucherToBookReferenceResult)
    .filter((item): item is CommandPaletteBookReferenceResult => item !== null);
}

/**
 * Registry of PREFIX → search handler for command-palette book-reference lookup.
 *
 * To add another journal (e.g. cash receipts):
 * 1. Add a `CommandPaletteBookReferenceKind` value.
 * 2. Implement `searchXBookReferences(context)` calling the list/detail API.
 * 3. Append `{ prefix: 'CR', accessPath: ROUTES...., search: searchCr... }` below.
 *
 * Prefix strings must match backend `formatTransactionReferenceNumber` (INV, CR, BILL, BILLPMT, …).
 */
export const COMMAND_PALETTE_BOOK_REFERENCE_HANDLERS: CommandPaletteBookReferenceHandler[] =
  [
    {
      prefix: 'JV',
      kind: 'journal_voucher',
      accessPath: ROUTES.TRANSACTIONS.ROOT,
      icon: ScrollText,
      search: searchJournalVoucherBookReferences,
    },
    {
      prefix: 'CAP',
      kind: 'capital',
      accessPath: ROUTES.CAPITALS.ROOT,
      icon: Landmark,
      search: searchCapitalBookReferences,
    },
    {
      prefix: 'PPE',
      kind: 'equipment',
      accessPath: ROUTES.EQUIPMENT.ROOT,
      icon: Wrench,
      search: searchEquipmentBookReferences,
    },
    {
      prefix: 'DEPR',
      kind: 'equipment_depreciation',
      accessPath: ROUTES.EQUIPMENT.ROOT,
      icon: Wrench,
      search: searchEquipmentDepreciationBookReferences,
    },
    {
      prefix: 'INVPMT',
      kind: 'ar_invoice_payment',
      accessPath: ROUTES.INVOICE_PAYMENTS.ROOT,
      icon: Receipt,
      search: searchArInvoicePaymentBookReferences,
    },
    {
      prefix: 'BILLPMT',
      kind: 'ap_bill_payment',
      accessPath: ROUTES.BILL_PAYMENTS.ROOT,
      icon: FileText,
      search: searchApBillPaymentBookReferences,
    },
    {
      prefix: 'FXAP',
      kind: 'ap_bill_fx_revaluation',
      accessPath: ROUTES.FX_REVALUATIONS.ROOT,
      icon: TrendingUp,
      search: searchApBillFxRevaluationBookReferences,
    },
    {
      prefix: 'INV',
      kind: 'ar_invoice',
      accessPath: ROUTES.AR_INVOICES.ROOT,
      icon: FileText,
      search: searchArInvoiceBookReferences,
    },
    {
      prefix: 'CR',
      kind: 'cash_receipt',
      accessPath: ROUTES.CASH_RECEIPTS_JOURNAL.ROOT,
      icon: Receipt,
      search: searchCashReceiptBookReferences,
    },
    {
      prefix: 'CD',
      kind: 'cash_disbursement',
      accessPath: ROUTES.CASH_DISBURSEMENT_JOURNAL.ROOT,
      icon: Wallet,
      search: searchCashDisbursementBookReferences,
    },
    {
      prefix: 'BILL',
      kind: 'ap_bill',
      accessPath: ROUTES.BILLS_LEDGER.ROOT,
      icon: FileText,
      search: searchApBillBookReferences,
    },
  ];

export function findCommandPaletteBookReferenceHandler(
  prefix: string,
): CommandPaletteBookReferenceHandler | undefined {
  return COMMAND_PALETTE_BOOK_REFERENCE_HANDLERS.find(
    (handler) => handler.prefix === prefix,
  );
}
